import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  LinearProgress
} from '@mui/material';
import EnhancedFilesTable from './EnhancedFilesTable';
import {
  CloudSync,
  CheckCircle,
  Error,
  Refresh,
  Image,
  Description,
  Settings,
  Save,
  Science
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface WidenConfigState {
  apiKey: string;
  baseUrl: string;
  isConfigured: boolean;
  lastSync?: string;
  totalAssets?: number;
  widenCount?: number;
}

interface SyncResult {
  status: string;
  syncedCount?: number;
  errorCount?: number;
  totalAssets?: number;
  message?: string;
}

interface JobProgress {
  current: number;
  total: number;
  percentage: number;
  message: string;
  details: any;
}

const WidenConfig: React.FC = () => {
  const [config, setConfig] = useState<WidenConfigState>({
    apiKey: '',
    baseUrl: '',
    isConfigured: false
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'success' | 'error'>('unknown');
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [uploadResult, setUploadResult] = useState<{ uploaded: number; failed: number; files: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedDocs, setUploadedDocs] = useState<any[]>([]);
  const [totalDocs, setTotalDocs] = useState<number>(0);
  const [polling, setPolling] = useState<boolean>(false);
  
  // Job progress tracking
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);
  const [progressInterval, setProgressInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadConfiguration();
    loadUploadedDocuments();
  }, []);

  // Poll for status updates while there are active docs
  useEffect(() => {
    let intervalId: any;
    if (polling) {
      intervalId = setInterval(() => {
        loadUploadedDocuments();
      }, 2000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [polling]);

  // Cleanup progress polling on unmount
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);

  const pollJobProgress = async (jobId: string) => {
    try {
      const response = await apiService.get(`/api/v1/sources/widen/job/${jobId}`);
      const job = response.data;

      setJobProgress({
        current: job.progress?.current || 0,
        total: job.progress?.total || 0,
        percentage: job.progress?.percentage || 0,
        message: job.progress?.message || 'Processing...',
        details: job.progress?.details || {}
      });

      if (job.status === 'completed') {
        setSyncing(false);
        setCurrentJobId(null);
        if (progressInterval) {
          clearInterval(progressInterval);
          setProgressInterval(null);
        }

        // Update sync result
        setSyncResult({
          status: 'success',
          syncedCount: job.result?.processed || 0,
          totalAssets: job.result?.total_assets || 0,
          message: `Sync completed! Added ${job.result?.added || 0}, updated ${job.result?.updated || 0}`
        });

        setMessage({ 
          type: 'success', 
          text: `✅ Sync completed! Processed ${job.result?.processed || 0} assets` 
        });

        // Reload documents and configuration
        setTimeout(() => {
          loadConfiguration();
          loadUploadedDocuments();
        }, 1000);

      } else if (job.status === 'failed') {
        setSyncing(false);
        setCurrentJobId(null);
        if (progressInterval) {
          clearInterval(progressInterval);
          setProgressInterval(null);
        }

        setMessage({ 
          type: 'error', 
          text: `❌ Sync failed: ${job.error_message || 'Unknown error'}` 
        });
      }
    } catch (error) {
      console.error('Error polling job progress:', error);
    }
  };

  const getErrorMessage = (error: any, fallback: string): string => {
    try {
      const detail = error?.response?.data?.detail;
      if (!detail) {
        return error?.message || fallback;
      }
      if (typeof detail === 'string') {
        return detail;
      }
      if (Array.isArray(detail)) {
        const messages = detail
          .map((d: any) => d?.msg || d?.message || JSON.stringify(d))
          .filter(Boolean);
        return messages.join('; ') || fallback;
      }
      if (typeof detail === 'object') {
        if (detail.msg || detail.message) {
          return detail.msg || detail.message;
        }
        return JSON.stringify(detail);
      }
      return String(detail);
    } catch {
      return fallback;
    }
  }

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/v1/sources/config');
      const widenConfig = response.data.find((source: any) => source.source_type === 'widen');
      
      if (widenConfig) {
        const isConfigured = Boolean(
          widenConfig.configuration?.api_key_configured &&
          (widenConfig.configuration?.base_url && widenConfig.configuration?.base_url !== 'Not configured')
        );
        setConfig((prev) => ({
          ...prev,
          apiKey: widenConfig.configuration.api_key_configured ? '••••••••' : '',
          baseUrl: widenConfig.configuration.base_url || '',
          isConfigured,
          lastSync: widenConfig.last_modified,
          totalAssets: widenConfig.configuration.total_assets
        }));
      }

      // Load document stats to populate counts
      try {
        const stats = await apiService.getDocumentStats();
        // Since manual uploads from Widen Config are now labeled as 'widen', 
        // we show all widen documents (manual + API synced) as one count
        const widenCount = stats?.by_source?.widen || 0;
        setConfig((prev) => ({ ...prev, widenCount, totalAssets: widenCount }));
      } catch {}

      // Load last sync time from recent jobs
      try {
        const jobsResponse = await apiService.get('/api/v1/sources/widen/jobs?limit=1');
        const jobs = jobsResponse?.data?.jobs || [];
        if (jobs.length > 0 && jobs[0].status === 'completed') {
          setConfig((prev) => ({ ...prev, lastSync: jobs[0].completed_at }));
        }
      } catch {}
    } catch (error) {
      console.error('Failed to load Widen configuration:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    if (!config.baseUrl) {
      setMessage({ type: 'error', text: 'Please fill in Base URL' });
      return;
    }
    
    if (!config.apiKey || (config.apiKey !== '••••••••' && config.apiKey.trim() === '')) {
      setMessage({ type: 'error', text: 'Please fill in API Key' });
      return;
    }

    try {
      setTesting(true);
      setMessage(null);
      
      const response = await apiService.post('/api/v1/sources/widen/test', {
        api_key: config.apiKey === '••••••••' ? undefined : config.apiKey,
        base_url: config.baseUrl
      });

      if (response.data.status === 'success') {
        setConnectionStatus('success');
        setMessage({ 
          type: 'success', 
          text: `✅ Connected successfully! Found ${response.data.total_assets || 0} assets` 
        });
      } else {
        setConnectionStatus('error');
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Connection failed' 
        });
      }
    } catch (error: any) {
      setConnectionStatus('error');
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Connection test failed')
      });
    } finally {
      setTesting(false);
    }
  };

  const saveConfiguration = async () => {
    if (!config.baseUrl) {
      setMessage({ type: 'error', text: 'Please fill in Base URL' });
      return;
    }
    
    if (!config.apiKey || (config.apiKey !== '••••••••' && config.apiKey.trim() === '')) {
      setMessage({ type: 'error', text: 'Please fill in API Key' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      await apiService.post('/api/v1/sources/widen/config', {
        api_key: config.apiKey === '••••••••' ? undefined : config.apiKey,
        base_url: config.baseUrl
      });

      setMessage({ type: 'success', text: 'Configuration saved successfully!' });
      setConfig(prev => ({ ...prev, isConfigured: true }));
      
      // Reload configuration to get updated status
      setTimeout(() => {
        loadConfiguration();
        loadUploadedDocuments();
      }, 1000);
      
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Failed to save configuration')
      });
    } finally {
      setLoading(false);
    }
  };

  const syncAssets = async () => {
    if (!config.isConfigured) {
      setMessage({ type: 'error', text: 'Please configure and test connection first' });
      return;
    }

    try {
      setSyncing(true);
      setMessage(null);
      setSyncResult(null);
      setJobProgress(null);

      // Start background sync job
      const response = await apiService.post('/api/v1/sources/widen/sync', { limit: 5000 });
      
      if (response.data.status === 'started') {
        const jobId = response.data.job_id;
        setCurrentJobId(jobId);
        
        setMessage({ 
          type: 'info', 
          text: '🔄 Widen sync started in background. Please wait...' 
        });

        // Start polling for progress
        const interval = setInterval(() => {
          pollJobProgress(jobId);
        }, 2000);
        setProgressInterval(interval);

        // Initial poll
        pollJobProgress(jobId);
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Failed to start sync job' 
        });
        setSyncing(false);
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Sync failed')
      });
      setSyncing(false);
    }
  };

  const handleManualUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      setMessage(null);
      setUploadResult(null);
      setJobProgress(null);

      const formData = new FormData();
      const fileNames: string[] = [];
      
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
        fileNames.push(files[i].name);
      }
      
      // Add source_type to specify this is from Widen config page
      formData.append('source_type', 'widen');

      const response = await apiService.postForm('/api/v1/documents/upload', formData);

      if (response.data.success) {
        const jobId = response.data.job_id;
        
        if (jobId) {
          // Start polling for job progress
          setCurrentJobId(jobId);
          pollJobProgress(jobId);
        } else {
          // Fallback to old behavior if no job_id
          setUploadResult({
            uploaded: response.data.uploaded || fileNames.length,
            failed: response.data.failed || 0,
            files: fileNames
          });
          setMessage({ 
            type: 'success', 
            text: `✅ Successfully uploaded ${response.data.uploaded || fileNames.length} files!` 
          });
          
          setTimeout(() => {
            setUploading(false);
            loadConfiguration();
            loadUploadedDocuments();
            apiService.getDocumentStats().then((stats) => {
              const manualCount = stats?.by_source?.franconnect || 0;
              const widenCount = stats?.by_source?.widen || 0;
              setConfig((prev) => ({ ...prev, manualCount, widenCount, totalAssets: manualCount + widenCount }));
            }).catch(() => {});
          }, 1500);
        }
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Upload failed' 
        });
        setUploading(false);
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Upload failed')
      });
    } finally {
      setUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const loadUploadedDocuments = async () => {
    try {
      // Load first page with reasonable amount
      const res = await apiService.getDocuments({ page: 1, per_page: 25, source_type: 'widen' });
      setUploadedDocs(res.documents || []);
      setTotalDocs(res.total || 0);
      const hasActive = (res.documents || []).some((d: any) => ['pending','processing'].includes((d.status || '').toString().toLowerCase()));
      setPolling(hasActive);
    } catch (e) {
      // ignore listing errors
    }
  };

  const handleLoadMoreDocuments = async (page: number, perPage: number, search?: string) => {
    const res = await apiService.getDocuments({ page, per_page: perPage, source_type: 'widen', search });
    return {
      documents: res.documents || [],
      total: res.total || 0
    };
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Image color="primary" />
          Widen DAM Configuration
        </Typography>
        
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4, fontSize: '1.1rem' }}>
          Configure your Widen Collective Digital Asset Management integration to enable media asset search and retrieval.
        </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

        <Box sx={{ display: 'flex', gap: 4, mb: 4, width: '100%', justifyContent: 'space-between', alignItems: 'stretch' }}>
          {/* Left Column - API Configuration */}
          <Card sx={{ flex: 1, minWidth: 420, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Settings />
                API Configuration
              </Typography>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Widen Base URL"
                  value={config.baseUrl}
                  onChange={(e) => setConfig(prev => ({ ...prev, baseUrl: e.target.value }))}
                  placeholder="https://your-company.widencollective.com"
                  helperText="Your Widen Collective instance URL"
                  disabled={loading}
                />
                
                <TextField
                  fullWidth
                  label="API Token"
                  type="password"
                  value={config.apiKey}
                  onChange={(e) => setConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                  placeholder="Enter your Widen API token"
                  helperText="Your Widen API authentication token"
                  disabled={loading}
                />
              </Stack>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={20} /> : <Science />}
                  onClick={testConnection}
                  disabled={testing || loading}
                >
                  Test Connection
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  onClick={saveConfiguration}
                  disabled={loading || testing}
                >
                  Save Configuration
                </Button>

                {connectionStatus === 'success' && (
                  <Chip icon={<CheckCircle />} label="Connected" color="success" size="small" />
                )}
                {connectionStatus === 'error' && (
                  <Chip icon={<Error />} label="Connection Failed" color="error" size="small" />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Right Column - Status Overview */}
          <Card sx={{ flex: 1, minWidth: 420, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Status
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Configuration</Typography>
                  <Chip 
                    label={config.isConfigured ? 'Configured' : 'Not Configured'} 
                    color={config.isConfigured ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Widen Assets</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    (Manual uploads + API synced)
                  </Typography>
                  <Typography variant="h6" color="primary">{config.widenCount || 0}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Last Sync</Typography>
                  <Typography variant="body2" color="text.secondary">{config.lastSync ? new Date(config.lastSync).toLocaleDateString() : 'Never'}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {config.isConfigured && (
          <Card sx={{ mb: 4, width: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudSync />
                Asset Management
              </Typography>

              <Typography variant="body2" color="text.secondary" paragraph>
                Sync your Widen assets or upload documents manually to enable search functionality. This will create vector embeddings for intelligent search.
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3, width: '100%', alignItems: 'stretch' }}>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Widen Sync
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Pull media assets from your Widen DAM
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={syncing ? <CircularProgress size={20} /> : <Refresh />}
                    onClick={syncAssets}
                    disabled={syncing || !config.isConfigured}
                    color="secondary"
                    fullWidth
                  >
                    {syncing ? 'Syncing Assets...' : 'Sync Assets Now'}
                  </Button>
                  
                  {/* Real-time Progress Display */}
                  {jobProgress && syncing && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {jobProgress.message}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {jobProgress.current}/{jobProgress.total}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={jobProgress.percentage} 
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {jobProgress.details?.processed && (
                          <Chip label={`Processed: ${jobProgress.details.processed}`} size="small" color="primary" />
                        )}
                        {jobProgress.details?.added && (
                          <Chip label={`Added: ${jobProgress.details.added}`} size="small" color="success" />
                        )}
                        {jobProgress.details?.updated && (
                          <Chip label={`Updated: ${jobProgress.details.updated}`} size="small" color="info" />
                        )}
                        {jobProgress.details?.failed && (
                          <Chip label={`Failed: ${jobProgress.details.failed}`} size="small" color="error" />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
                
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Manual Upload
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload documents, images, and videos directly from your computer
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={uploading ? <CircularProgress size={20} /> : <Description />}
                    onClick={handleManualUpload}
                    disabled={loading || uploading}
                    color="primary"
                    fullWidth
                  >
                    {uploading ? 'Uploading...' : 'Upload Documents'}
                  </Button>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.pptx,.jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.wmv,.flv,.webm,.mkv"
                    style={{ display: 'none' }}
                  />
                  
                  {/* Real-time Upload Progress Display */}
                  {uploading && jobProgress && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {jobProgress.message}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {jobProgress.current}/{jobProgress.total}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={jobProgress.percentage} 
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {jobProgress.details?.succeeded && (
                          <Chip label={`Succeeded: ${jobProgress.details.succeeded}`} size="small" color="success" />
                        )}
                        {jobProgress.details?.failed && (
                          <Chip label={`Failed: ${jobProgress.details.failed}`} size="small" color="error" />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

            {syncResult && (
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>Sync Results:</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText 
                      primary={`${syncResult.syncedCount || 0} assets synced successfully`}
                    />
                  </ListItem>
                  {syncResult.errorCount && syncResult.errorCount > 0 && (
                    <ListItem>
                      <ListItemIcon><Error color="error" /></ListItemIcon>
                      <ListItemText 
                        primary={`${syncResult.errorCount} assets had errors`}
                      />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemIcon><Description /></ListItemIcon>
                    <ListItemText 
                      primary={`Total: ${syncResult.totalAssets || 0} assets processed`}
                    />
                  </ListItem>
                </List>
              </Paper>
            )}

            {uploadResult && (
              <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Upload Results:</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText 
                      primary={`${uploadResult.uploaded} files uploaded successfully`}
                    />
                  </ListItem>
                  {uploadResult.failed > 0 && (
                    <ListItem>
                      <ListItemIcon><Error color="error" /></ListItemIcon>
                      <ListItemText 
                        primary={`${uploadResult.failed} files failed to upload`}
                      />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemIcon><Description /></ListItemIcon>
                    <ListItemText 
                      primary="Files processed:"
                      secondary={uploadResult.files.join(', ')}
                    />
                  </ListItem>
                </List>
              </Paper>
            )}
          </CardContent>
        </Card>
        )}

        {/* Results Section */}
        {(syncResult || uploadResult) && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Processing Results
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {syncResult && (
                  <Box sx={{ flex: 1, minWidth: 300 }}>
                    <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Widen Sync Results
                      </Typography>
                      <List dense>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText 
                            primary={`${syncResult.syncedCount || 0} assets synced successfully`}
                          />
                        </ListItem>
                        {syncResult.errorCount && syncResult.errorCount > 0 && (
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon><Error color="error" /></ListItemIcon>
                            <ListItemText 
                              primary={`${syncResult.errorCount} assets had errors`}
                            />
                          </ListItem>
                        )}
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon><Description /></ListItemIcon>
                          <ListItemText 
                            primary={`Total: ${syncResult.totalAssets || 0} assets processed`}
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Box>
                )}

                {uploadResult && (
                  <Box sx={{ flex: 1, minWidth: 300 }}>
                    <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Upload Results
                      </Typography>
                      <List dense>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText 
                            primary={`${uploadResult.uploaded} files uploaded successfully`}
                          />
                        </ListItem>
                        {uploadResult.failed > 0 && (
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon><Error color="error" /></ListItemIcon>
                            <ListItemText 
                              primary={`${uploadResult.failed} files failed to upload`}
                            />
                          </ListItem>
                        )}
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon><Description /></ListItemIcon>
                          <ListItemText 
                            primary="Files processed:"
                            secondary={uploadResult.files.join(', ')}
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Uploaded Files Section */}
        <Box sx={{ mt: 3 }}>
          <EnhancedFilesTable 
            files={uploadedDocs} 
            title="Widen Files"
            onLoadMore={handleLoadMoreDocuments}
            totalCount={totalDocs}
            serverSidePagination={true}
          />
        </Box>
    </Box>
  );
};

export default WidenConfig;
